import type { MetricStatus } from './metric-status.js';
import { statusColorVar } from './metric-status.js';
import { localDateIso } from './dates.js';

export const LEGAL_DOCUMENT_TYPES = [
  'contract',
  'annex',
  'nda',
  'power_of_attorney',
  'statutory',
] as const;
export type LegalDocumentTypeDto = (typeof LEGAL_DOCUMENT_TYPES)[number];

export const LEGAL_DOCUMENT_STATUSES = ['draft', 'active', 'terminated'] as const;
export type LegalDocumentStatusDto = (typeof LEGAL_DOCUMENT_STATUSES)[number];

export const LEGAL_DOCUMENT_TYPE_LABELS: Record<LegalDocumentTypeDto, string> = {
  contract: 'Договор',
  annex: 'Приложение',
  nda: 'NDA',
  power_of_attorney: 'Доверенность',
  statutory: 'Уставной документ',
};

export const LEGAL_DOCUMENT_STATUS_LABELS: Record<LegalDocumentStatusDto, string> = {
  draft: 'Черновик',
  active: 'Действует',
  terminated: 'Прекращён',
};

export const LEGAL_EXPIRY_WARN_DAYS = 60;

export interface LegalDocumentFields {
  type: LegalDocumentTypeDto;
  clientId: string | null;
  parentId: string | null;
  validFrom: string | null;
  validTo: string | null;
}

export interface LegalParentContext {
  id: string;
  type: LegalDocumentTypeDto;
  clientId: string | null;
}

export function daysUntil(isoDate: string, todayIso = localDateIso()): number {
  const target = new Date(`${isoDate}T12:00:00`);
  const today = new Date(`${todayIso}T12:00:00`);
  return Math.floor((target.getTime() - today.getTime()) / 86_400_000);
}

export function expiresInDays(validTo: string | null, todayIso = localDateIso()): number | null {
  if (!validTo) return null;
  return daysUntil(validTo, todayIso);
}

export function isExpired(validTo: string | null, todayIso = localDateIso()): boolean {
  if (!validTo) return false;
  return validTo < todayIso;
}

export function isExpiringSoon(validTo: string | null, todayIso = localDateIso()): boolean {
  const left = expiresInDays(validTo, todayIso);
  if (left === null) return false;
  return left <= LEGAL_EXPIRY_WARN_DAYS;
}

/** Map expiry horizon to chart status tokens for styling. */
export function expiryStatus(validTo: string | null, todayIso = localDateIso()): MetricStatus {
  const left = expiresInDays(validTo, todayIso);
  if (left === null) return 'na';
  if (left < 0) return 'bad';
  if (left <= 14) return 'warn';
  if (left <= LEGAL_EXPIRY_WARN_DAYS) return 'ok';
  return 'good';
}

export function expiryColorVar(validTo: string | null, todayIso = localDateIso()): string {
  return statusColorVar(expiryStatus(validTo, todayIso));
}

export function validateLegalDocumentFields(
  fields: LegalDocumentFields,
  parent: LegalParentContext | null,
): string | null {
  if (fields.type === 'statutory') {
    if (fields.clientId) return 'Уставной документ не привязан к клиенту';
    if (fields.parentId) return 'У родительского документа не может быть parentId';
  } else if (!fields.clientId) {
    return 'Укажите клиента';
  }

  if (fields.type === 'annex') {
    if (!fields.parentId) return 'Приложение должно ссылаться на договор';
    if (!parent) return 'Родительский договор не найден';
    if (parent.type !== 'contract') return 'Родитель должен быть договором';
    if (parent.clientId !== fields.clientId) {
      return 'Клиент приложения должен совпадать с договором';
    }
  } else if (fields.parentId) {
    return 'parentId допустим только для приложений';
  }

  if (fields.validFrom && fields.validTo && fields.validTo < fields.validFrom) {
    return 'Дата окончания не может быть раньше начала';
  }

  return null;
}
